import { HttpService } from "@app/core/services/http/http.service";
import { Injectable, WritableSignal, inject, signal } from "@angular/core";
import { MatBottomSheetConfig } from "@angular/material/bottom-sheet";
import { MatDialogConfig } from "@angular/material/dialog";
import { DocumentsPlusInBasketAttachComponent } from "@app/app-modules/documents-plus/documents-plus-in-basket/documents-plus-in-basket-attach/documents-plus-in-basket-attach.component";
import {
  InBasket,
  InBasketListRequestAdapter,
  InBasketListResponseAdapter,
  InBasketDelete,
  InBasketDeleteAdapter,
  InBasketAssignUser,
  InBasketAssignUserAdapter,
  InBasketDocumentTypesAdapter,
  InBasketAttach,
  InBasketAttachAdapter,
  InBasketMaintenance,
  InBasketMaintenanceAdapter,
  InBasketCountAdapter,
  InBasketHistoryListAdapter,
  InBasketHistoryList,
  InBasketCount,
  InBasketMergedDocumentsAdapter,
  InBasketMerge
} from "@app/app-modules/documents-plus/documents-plus-in-basket/documents-plus-in-basket.model";
import { DocumentsPlusAttachParms } from "@app/app-modules/documents-plus/documents-plus.model";
import { ModalSheetService } from "@app/core/services/modal-sheet.service";
import { FileTypes } from "@app/shared/constants/file-types.constants";
import { DPLUS_URL } from "@app/shared/constants/url.constants";
import { map } from "rxjs";

@Injectable({
  providedIn: "root"
})
export class DocumentsPlusInBasketService {
  inBasketCount: WritableSignal<InBasketCount> = signal<InBasketCount>({
    totalDocuments: 0,
    totalUnreadDocuments: 0
  });

  private _acceptedFileTypes: string;
  private http = inject(HttpService);
  private modalSheetService = inject(ModalSheetService);

  constructor() {
    this._setAcceptedFileTypes();
  }

  /**
   * Fetch In Basket list and user id
   * @param parms
   * @returns
   */
  fetchInBasket(parms: InBasket) {
    const reqAdapter = new InBasketListRequestAdapter();
    const resAdapter = new InBasketListResponseAdapter();
    return this.http
      .addDataParams(reqAdapter.adapt(parms))
      .get<InBasket>(DPLUS_URL.INBASKET_LIST)
      .pipe(map((response: any) => resAdapter.adapt(response)));
  }

  /**
   * Remove document from In Basket list
   * @param parms
   * @returns
   */
  deleteDocument(uniqueId: string[]) {
    const adapter = new InBasketDeleteAdapter();
    return this.http
      .addDataParams({ uniqueId } as InBasketDelete)
      .delete(DPLUS_URL.INBASKET_DELETE)
      .pipe(map((response: any) => adapter.adapt(response)));
  }

  /**
   * Assign document to a different user
   * @param parms
   * @returns
   */
  assignUser(parms: InBasketAssignUser) {
    const adapter = new InBasketAssignUserAdapter();
    return this.http
      .addDataParams(parms)
      .put<InBasketAssignUser>(DPLUS_URL.INBASKET_ASSIGN, "")
      .pipe(map((response: any) => adapter.adapt(response)));
  }

  /**
   * Fetch document types for assigning uploaded documents
   * @param parms
   * @returns
   */
  fetchDocumentTypes() {
    const adapter = new InBasketDocumentTypesAdapter();
    return this.http.get(DPLUS_URL.INBASKET_DOCUMENT_TYPES).pipe(map((response: any) => adapter.adapt(response)));
  }

  /**
   * Attach an existing document
   * @returns
   */
  attachDocument(parms: InBasketAttach, skipLoader: boolean = false) {
    const adapter = new InBasketAttachAdapter();
    return this.http
      .skipLoaderHandler(skipLoader)
      .addDataParams(parms)
      .post<InBasketAttach>(DPLUS_URL.INBASKET_ATTACH, "")
      .pipe(map((response: any) => adapter.adapt(response)));
  }

  fetchDocument(parms: InBasketMaintenance) {
    const adapter = new InBasketMaintenanceAdapter();
    return this.http
      .addDataParams(parms)
      .get<InBasketMaintenance>(DPLUS_URL.INBASKET_MAINTENANCE)
      .pipe(map((response: any) => adapter.adapt(response)));
  }

  updateDocument(parms: InBasketMaintenance) {
    const adapter = new InBasketMaintenanceAdapter();
    return this.http
      .addDataParams(parms)
      .put<InBasketMaintenance>(DPLUS_URL.INBASKET_MAINTENANCE, "")
      .pipe(map((response: any) => adapter.adapt(response)));
  }

  fetchDocumentCount() {
    const respAdapter = new InBasketCountAdapter();
    return this.http.get<InBasketCount>(DPLUS_URL.INBASKET_COUNT).pipe(map((response: any) => respAdapter.adapt(response)));
  }

  fetchInBasketHistory(fileId: string) {
    const respAdapter = new InBasketHistoryListAdapter();
    return this.http
      .addDataParams({ fileId: fileId })
      .get<InBasketHistoryList[]>(DPLUS_URL.INBASKET_HISTORY_LIST)
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  fetchMergedDocuments(fileList: string[]) {
    const respAdapter = new InBasketMergedDocumentsAdapter();
    return this.http
      .addDataParams({ fileList })
      .post<InBasketMerge>(DPLUS_URL.INBASKET_MERGE_PDF, "")
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  /**
   * Update read status for records.
   * @param data
   * @param markAsRead
   * @returns
   */
  updateReadStatus(data: string[], markAsRead: boolean) {
    return this.http.addDataParams({ documentId: data, mode: markAsRead }).put(DPLUS_URL.INBASKET_MARK_READ_UNREAD, "");
  }

  /**
   * Encapsulated method to attach documents
   * @param applicationId
   * @param hideDelete
   * @param row
   * @param docPlusParms
   * @param selection
   */
  doAttach(docPlusParms: DocumentsPlusAttachParms) {
    const config = this.modalSheetService.isDesktop ? new MatDialogConfig() : new MatBottomSheetConfig();
    config.data = docPlusParms;
    config.hasBackdrop = !docPlusParms?.autoAttach && !docPlusParms.index;
    return this.modalSheetService.open(DocumentsPlusInBasketAttachComponent, config);
  }

  get acceptedFileTypes(): string {
    return this._acceptedFileTypes;
  }

  /**
   * Set the accepted file types
   */
  private _setAcceptedFileTypes() {
    const types = [
      `.${FileTypes.BMP.toUpperCase()}`,
      `.${FileTypes.JPG.toUpperCase()}`,
      `.${FileTypes.JPEG.toUpperCase()}`,
      `.${FileTypes.M4A.toUpperCase()}`,
      `.${FileTypes.MP3.toUpperCase()}`,
      `.${FileTypes.MP4.toUpperCase()}`,
      `.${FileTypes.ADOBE.toUpperCase()}`,
      `.${FileTypes.PNG.toUpperCase()}`,
      `.${FileTypes.TIF.toUpperCase()}`,
      `.${FileTypes.TIFF.toUpperCase()}`,
      `.${FileTypes.WAV.toUpperCase()}`,
      `.${FileTypes.WEBP.toUpperCase()}`
    ];
    this._acceptedFileTypes = types.join(", ");
  }
}
